import { Fragment, memo, useCallback, useEffect } from 'react';
import { useFormikContext } from 'formik';
import { Typography } from '@mui/material';
import { TypeOperationData } from 'shell/models/type-operation';
import { useEventCallback } from '@mui/material';
import Table from 'shell/components/shared/table';
import { headCells } from './constants';
import { useNavigate } from 'react-router-dom';
import { AlertModal } from 'system/components/shared/delete-alert/types';
import {
  HttpRequestFunction,
  RequestOptionsOverride,
  ResponsePromise,
} from 'shell/hooks/use-http-fetch/stateless/type';
import { HttpBackendError } from 'shell/error/definitions/http-backend-error';
import { HttpResponseList } from 'shell/models/http-response';
import { transformDataset } from 'practice/pages/type-operation/utils';
import {
  BodyResearchTypeOperation,
  ResearchTypeOperationVM,
  ResearchTypeOperationTableVM,
} from 'practice/view-models/research-type-operation';

interface TableTypeOperationProps {
  activeDelete?: boolean;
  activeEdit?: boolean;
  getTypeOperation:
    | ((options?: RequestOptionsOverride | undefined) => Promise<Error | HttpBackendError>)
    | HttpRequestFunction<HttpResponseList<TypeOperationData>, BodyResearchTypeOperation[]>;
  typeOperation: Array<BodyResearchTypeOperation>;
  setDeleteAlertModal: (value: AlertModal<BodyResearchTypeOperation>) => void;
}

const TableTypeOperationComponent = ({
  activeDelete,
  activeEdit,
  typeOperation,
  getTypeOperation,
  setDeleteAlertModal,
}: TableTypeOperationProps): React.ReactElement => {
  const formContext = useFormikContext<ResearchTypeOperationVM>();

  const navigate = useNavigate();

  const onChange = useEventCallback(
    (
      key: keyof ResearchTypeOperationTableVM,
      value: ResearchTypeOperationTableVM[keyof ResearchTypeOperationTableVM]
    ) => {
      formContext.setFieldValue('table', {
        ...(formContext.values.table || {}),
        [key]: value,
      });
    }
  );

  const onChanges = useEventCallback((values: ResearchTypeOperationTableVM) => {
    formContext.setFieldValue('table', {
      ...(formContext.values.table || {}),
      ...values,
    });
  });

  const onChangePage = useEventCallback((page: number) => {
    onChange('page', page || 0);
  });

  const onChangeRowsPerPage = useEventCallback((rowsPerPage: number) => {
    onChanges({
      rowsPerPage: rowsPerPage || 0,
      page: 0,
    });
  });

  const onVisibilityPress = useEventCallback(
    (cell: BodyResearchTypeOperation): Promise<BodyResearchTypeOperation> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-tipo-operazione/dettaglio/${cell?.practice}/${cell?.code}`);
      });
    }
  );

  const onEditPress = useEventCallback(
    (cell: BodyResearchTypeOperation): Promise<BodyResearchTypeOperation> => {
      return new Promise(() => {
        navigate(`/pratiche/ricerca-tipo-operazione/modifica/${cell?.practice}/${cell?.code}`);
      });
    }
  );

  const onTrashPress = useCallback(
    (cell: BodyResearchTypeOperation): Promise<BodyResearchTypeOperation> => {
      return new Promise((resolve) => {
        setDeleteAlertModal({ open: true, element: cell });
        resolve(cell);
      }) as Promise<BodyResearchTypeOperation>;
    },
    [setDeleteAlertModal]
  );

  useEffect(() => {
    if (typeOperation?.length > 0) {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: typeOperation,
      });
    } else {
      formContext.setFieldValue('table', {
        ...formContext.values?.table,
        body: typeOperation,
      });
    }
  }, [typeOperation]);

  const onSort = useCallback(
    (orders) => {
      const newDataset = {
        ...formContext.values,
        filter: {
          ...formContext.values?.filter,
          orders,
        },
      };
      const queryString = transformDataset(newDataset);
      (
        getTypeOperation({
          params: {},
          paramsSerializer: () => queryString,
        }) as ResponsePromise<HttpResponseList<TypeOperationData>, Array<BodyResearchTypeOperation>>
      ).then(() => {
        formContext.setFieldValue('filter', {
          ...{
            ...formContext.values?.filter,
            orders,
          },
        });
      });
    },
    [formContext.setFieldValue, getTypeOperation, formContext.values]
  );

  return (
    <Fragment>
      {formContext.values.table?.body ? (
        (formContext.values.table?.body || [])?.length > 0 ? (
          <Table
            ariaLabel="Lista tipi operazione"
            page={formContext.values.table?.page || 0}
            rowsPerPage={formContext.values.table?.rowsPerPage}
            body={formContext.values.table?.body || []}
            count={(formContext.values.table?.body || [])?.length}
            head={headCells}
            onSort={onSort}
            onPageChange={onChangePage}
            onRowsPerPageChange={onChangeRowsPerPage}
            onVisibilityPress={onVisibilityPress}
            onEditPress={onEditPress}
            onTrashPress={onTrashPress}
            orders={formContext.values.filter?.orders}
            isNotLayoutFixed
            activeActions={{
              delete: activeDelete,
              read: true,
              edit: activeEdit,
            }}
          />
        ) : (
          <Typography color="secondary.main">
            Nessun elemento trovato con la ricerca effettuata.
          </Typography>
        )
      ) : undefined}
    </Fragment>
  );
};

TableTypeOperationComponent.displayName = 'Table Type Operation component';

const TableTypeOperation = memo(TableTypeOperationComponent);

export default TableTypeOperation;
